import React from 'react'
import { connect } from 'react-redux'
import { Button } from 'semantic-ui-react'
import styled from 'styled-components'

import MapViewActions from '../../reducers/mapViewReducer'

import MapView from './components/MapView'
import MapControl from './components/Control'
import Container from './components/Container'
import DriverLocations from './components/DriverLocations'
import LiveTracking from './components/LiveTracking'
import ExpressLiveTracking from './components/ExpressLiveTracking'
import OrderMap from './components/OrderMap'
import MerchantMap from './components/MerchantMap'
import Cluster from './components/Cluster'
import OrderGuideByCustomer from './components/OrderGuideByCustomer'
import OrderGuideByMerchant from './components/OrderGuideByMerchant'
import SameDayOrderGuide from './components/SameDayOrderGuide'

import helpers from './helpers'

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: calc(85vh - 56px);
`

class MuuveMap extends React.PureComponent {
  mapView = null

  state = {
    mapReady: false,
    driverType: 'all',
    trafficLayer: false,
    polygonLayer: false,
  }

  componentDidMount() {
    document.addEventListener('keyup', this.handleKeyUp)
  }

  componentDidUpdate(prevProps) {
    const { view: prevView } = prevProps
    const { view } = this.props

    if (prevView === null && view) {
      const getBounds = {
        drivers: this.getDriverBounds,
        live_tracking: this.getLiveTrackingBounds,
        express_live_tracking: this.getExpressLiveTrackingBounds,
        merchant: this.getMerchantBounds,
        order: this.getOrderBounds,
      }[view]

      if (getBounds) {
        const bounds = getBounds()
        this.mapView.mapViewRef.fitBounds(bounds)
      }
    }
  }

  onCreateGroup = () => {
    if (this.orderGuideRef) {
      const success = this.orderGuideRef.onCreateGroup()

      if (success) this.closeMap()
    }
  }

  onClearMarkers = () => {
    if (this.orderGuideRef) {
      this.orderGuideRef.onClearMarkers()
    }
  }

  handleKeyUp = event => {
    const { view } = this.props
    if (event.defaultPrevented || view === null) return

    const key = event.key || event.keyCode

    if (key === 'Escape' || key === 'Esc' || key === 27) {
      this.closeMap()
    }
  }

  closeMap = () => {
    const { setMapData } = this.props

    setMapData(null, null)
  }

  getExpressLiveTrackingBounds = () => {
    const { drivers, data } = this.props

    const { google } = window
    const bounds = new google.maps.LatLngBounds()
    const driver = drivers[data.driverUid]
    const { receiverLocations = [] } = data

    bounds.extend(helpers.latLng(google, driver.location))
    bounds.extend(helpers.latLng(google, data.merchantLocation))

    receiverLocations.forEach(receiverLocation => bounds.extend(helpers.latLng(google, receiverLocation)))

    return bounds
  }

  getLiveTrackingBounds = () => {
    const { drivers, data } = this.props
    const { google } = window
    const bounds = new google.maps.LatLngBounds()
    const driver = drivers[data.driverUid]

    bounds.extend(helpers.latLng(google, driver.location))
    bounds.extend(helpers.latLng(google, data.deliveryLocation))
    bounds.extend(helpers.latLng(google, data.merchantLocation))

    return bounds
  }

  getMerchantBounds = () => {
    const { data } = this.props
    const { google } = window
    const bounds = this.getDriverBounds()

    bounds.extend(helpers.latLng(google, data.merchantLocation))
    bounds.extend(helpers.latLng(google, data.deliveryLocation))

    return bounds
  }

  getDriverBounds = () => {
    const { drivers } = this.props
    const { google } = window
    const bounds = new google.maps.LatLngBounds()

    const driverKeys = Object.keys(drivers)
    const driverCount = driverKeys.length

    for (let index = 0; index < driverCount; index += 1) {
      const driverKey = driverKeys[index]
      const driver = drivers[driverKey]

      if (driver.online && driver.location) {
        bounds.extend(helpers.latLng(google, driver.location))
      }
    }

    return bounds
  }

  getOrderBounds = () => {
    const { data } = this.props
    const { google } = window
    const bounds = new google.maps.LatLngBounds()

    bounds.extend(helpers.latLng(google, data.deliveryLocation))
    bounds.extend(helpers.latLng(google, data.requestCoords))

    return bounds
  }

  renderMarkers = () => {
    const { drivers, view, data } = this.props
    const { driverType } = this.state

    switch (view) {
      case 'drivers':
        return <DriverLocations drivers={drivers} driverType={driverType} />
      case 'express_live_tracking':
        return <ExpressLiveTracking drivers={drivers} data={data} />
      case 'live_tracking':
        return <LiveTracking drivers={drivers} data={data} />
      case 'order':
        return <OrderMap data={data} />
      case 'merchant':
        return (
          <React.Fragment>
            <DriverLocations drivers={drivers} driverType={driverType} />
            <MerchantMap data={data} drivers={drivers} />
          </React.Fragment>
        )
      case 'customer_zone':
      case 'merchant_zone':
        return <Cluster clusterMarkers={data} />
      case 'order_guide_by_merchant':
        return (
          <OrderGuideByMerchant
            onMounted={ref => {
              this.orderGuideRef = ref
            }}
            orders={data.orders}
            groupedOrderKeys={data.groupedOrderKeys}
            createNewGroup={data.createNewGroup}
          />
        )
      case 'order_guide_by_customer':
        return (
          <OrderGuideByCustomer
            onMounted={ref => {
              this.orderGuideRef = ref
            }}
            orders={data.orders}
            groupedOrderKeys={data.groupedOrderKeys}
            createNewGroup={data.createNewGroup}
          />
        )
      case 'same_day_order_guide':
        return (
          <SameDayOrderGuide
            onMounted={ref => {
              this.orderGuideRef = ref
            }}
            groupedOrderKeys={data.groupedOrderKeys}
            createNewGroup={data.createNewGroup}
          />
        )
      default:
        return null
    }
  }

  renderMapControl = () => {
    const { mapReady, trafficLayer, polygonLayer, driverType } = this.state
    const { google } = window

    if (!mapReady) return null

    const buttonStyle = active => ({
      marginRight: 5,
      marginTop: 10,
      backgroundColor: active ? 'gray' : 'white',
      color: active ? 'white' : 'rgba(0,0,0,.8)',
    })

    return (
      <MapControl context={this.mapView.mapViewRef.context} position={google.maps.ControlPosition.TOP_RIGHT}>
        <Button
          onClick={() => this.setState({ trafficLayer: !trafficLayer })}
          style={buttonStyle(trafficLayer)}
        >
          Traffic
        </Button>
        <Button
          onClick={() => this.setState({ polygonLayer: !polygonLayer })}
          style={buttonStyle(polygonLayer)}
        >
          District
        </Button>
        <Button
          onClick={() => this.setState({ driverType: driverType === 'available' ? 'all' : 'available' })}
          style={{ ...buttonStyle(driverType === 'available'), marginRight: 10 }}
        >
          Available Drivers
        </Button>
      </MapControl>
    )
  }

  renderOrderGuideControl = () => {
    const { google } = window

    return (
      <MapControl context={this.mapView.mapViewRef.context} position={google.maps.ControlPosition.TOP_RIGHT}>
        <Button
          onClick={this.onCreateGroup}
          style={{
            marginRight: 10,
            marginTop: 10,
            backgroundColor: 'white',
            color: 'rgba(0,0,0,.8)',
          }}
        >
          Create Group
        </Button>
        <Button color="red" onClick={this.onClearMarkers} style={{ marginRight: 10 }}>
          Clear
        </Button>
      </MapControl>
    )
  }

  render() {
    const { view } = this.props
    const { trafficLayer, polygonLayer } = this.state

    let title = 'Map'
    if (view === 'merchant_zone') title = 'Merchant Zone'
    else if (view === 'customer_zone') title = 'Customer Zone'

    const isOrderGuide = view === 'order_guide_by_merchant' || view === 'order_guide_by_customer' || view === 'same_day_order_guide'

    return (
      <Container onClose={this.closeMap} title={title}>
        <MapContainer>
          <MapView
            onReady={() => this.setState({ mapReady: true })}
            onMapMount={ref => {
              this.mapView = ref
            }}
            trafficLayer={trafficLayer}
            polygonLayer={polygonLayer}
          >
            {this.renderMarkers()}
            {isOrderGuide ? this.renderOrderGuideControl() : this.renderMapControl()}
          </MapView>
        </MapContainer>
      </Container>
    )
  }
}

const mapState = ({ mapView, drivers }) => ({
  view: mapView.view,
  data: mapView.data,
  drivers: drivers.drivers,
})

const mapDispatch = {
  setMapData: MapViewActions.setMapData,
}

export default connect(
  mapState,
  mapDispatch
)(MuuveMap)



// WEBPACK FOOTER //
// ./src/containers/mapView/index.js